/**
 * User Store
 * 
 * Manages current user context, role assignments, and permission state.
 * 
 * PERSISTENCE STRATEGY:
 * - User selection (userId, userName, role) persisted to localStorage so it survives refresh and is shared across tabs (new tabs don’t redirect to login)
 * - Supabase remains the source of truth for permissions and role assignments
 */

import { create } from 'zustand';
import type { ActorRole } from '../../types/prioritization/roles';
import { isPlatformOrSystemAdmin } from '../../types/prioritization/roles';
import type { SystemModelingSpace } from '../../types/prioritization/spaces';
import type {
  PermissionAction,
  PermissionScope,
  ScopedRoleAssignment,
  GroupMembership,
  EffectivePermission,
} from '../../types/prioritization/permissions';
import {
  loadUserPermissionData,
  checkPermission,
  getEffectivePermissions,
  getResolvedCapabilitiesForUser,
  canSync,
  clearPermissionCache,
} from '../../lib/permissionResolver';
import {
  roleHasPermission,
  roleHasAnyPermission,
  roleCanAccessNavSection,
  roleCanAccessNavItem,
  toActorRole,
  NAV_SECTION_PERMISSIONS,
  NAV_ITEM_PERMISSIONS,
  type NavSectionId,
} from '../../types/prioritization/rolePermissionMapping';
import { getSupabaseClient, isSupabaseConfigured } from '../../lib/supabaseClient';
import { isSuperAdmin, isSuperAdminBySupabaseUserId } from '../../lib/superAdminService';
import {
  fetchAll,
  updateRecord,
  isSupabaseAvailable,
  createInitialSyncState,
  type SyncState,
} from '../../lib/supabaseSync';
import { useTenantStore } from './tenantStore';
import { subscribeWorkspaceContext } from '../../lib/workspaceContextEvents';
import { loadAuthoredPoliciesForWorkspace } from '../../lib/authorization/authoredPolicyLoader';
import {
  checkAuthorization,
  explainAuthorization,
  type UnifiedAuthResult,
  type UnifiedAuthInput,
} from '../../lib/authorization/unifiedAuthorization';
import type { ExplainResult } from '../../lib/authorization/types';
import { isImpersonationEnabled } from '../../lib/impersonationConfig';
import { loadFeaturedJobRoleSlugs, saveFeaturedJobRoleSlugs } from '../../lib/featuredJobRoles';
import { jobSlugsFromAssignments } from '../../lib/contributorNavProfiles';

const USER_PROFILES_TABLE = 'user_profiles';
const ROLE_ASSIGNMENTS_TABLE = 'role_assignments';
const GROUP_MEMBERSHIPS_TABLE = 'group_memberships';

// ============================================================================
// User Selection Persistence (localStorage = shared across tabs)
// ============================================================================

const USER_SELECTION_KEY = 'peak-priorities-user-selection';

function getStorage(): Storage | undefined {
  return typeof localStorage !== 'undefined' ? localStorage : undefined;
}

interface PersistedUserSelection {
  userId: string;
  userName: string;
  userEmail: string;
  currentRole: ActorRole | null;
  assignedRoleNames?: string[];
}

interface ImpersonationState {
  isImpersonating: boolean;
  originalUserId: string;
  originalUserName: string;
  originalUserEmail: string;
  originalRole: ActorRole;
  originalTenantId: string | null;
  originalWorkspaceId: string | null;
  impersonatedUserId: string;
  impersonatedUserName: string;
  impersonatedUserEmail: string;
  impersonatedRole: ActorRole;
}

const IMPERSONATION_KEY = 'peak-priorities-impersonation';

/** When impersonation is disabled, clear any stored impersonation and any user selection that may have been saved while impersonating (so no one is left "stuck" as an admin). */
function clearImpersonationStorageIfDisabled(): void {
  const session = getSessionStorage();
  if (!session) return;
  if (!isImpersonationEnabled()) {
    try {
      const hadImpersonation = session.getItem(IMPERSONATION_KEY);
      session.removeItem(IMPERSONATION_KEY);
      const storage = getStorage();
      if (hadImpersonation && storage) {
        storage.removeItem(USER_SELECTION_KEY);
      }
    } catch (_) {
      // ignore
    }
  }
}

function getSessionStorage(): Storage | undefined {
  return typeof sessionStorage !== 'undefined' ? sessionStorage : undefined;
}

function saveImpersonation(state: ImpersonationState | null): void {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    if (state) {
      if (!isImpersonationEnabled()) return; // never persist new impersonation when feature is off
      storage.setItem(IMPERSONATION_KEY, JSON.stringify(state));
    } else {
      // Always allow clearing the key so stale data is removed when feature is off
      storage.removeItem(IMPERSONATION_KEY);
    }
  } catch (e) {
    console.warn('[UserStore] Failed to save impersonation state:', e);
  }
}

function loadImpersonation(): ImpersonationState | null {
  const storage = getSessionStorage();
  if (!storage) return null;
  try {
    const stored = storage.getItem(IMPERSONATION_KEY);
    if (stored) {
      return JSON.parse(stored) as ImpersonationState;
    }
  } catch (e) {
    console.warn('[UserStore] Failed to load impersonation state:', e);
  }
  return null;
}

// Clear stored impersonation before we read it, so disabled = no data to apply
clearImpersonationStorageIfDisabled();
const persistedImpersonation = loadImpersonation();
/** Effective impersonation only when feature is enabled; otherwise ignore stored state */
function getEffectiveImpersonation(): ImpersonationState | null {
  if (!isImpersonationEnabled() || !persistedImpersonation?.isImpersonating) return null;
  return persistedImpersonation;
}

function saveUserSelection(selection: PersistedUserSelection): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(USER_SELECTION_KEY, JSON.stringify(selection));
  } catch (e) {
    console.warn('[UserStore] Failed to save user selection:', e);
  }
}

/**
 * Remove the saved user selection so the next page load starts cold and adopts
 * whoever the Supabase session names. Leaves the in-memory store as it is.
 */
export function forgetSavedUserSelection(): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(USER_SELECTION_KEY);
  } catch (_) {
    // ignore
  }
}

function loadUserSelection(): PersistedUserSelection | null {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const stored = storage.getItem(USER_SELECTION_KEY);
    if (stored) {
      return JSON.parse(stored) as PersistedUserSelection;
    }
  } catch (e) {
    console.warn('[UserStore] Failed to load user selection:', e);
  }
  return null;
}

// Load persisted user selection or use defaults
const persistedUser = loadUserSelection();
// Default to a safe non-admin role for new/unknown users
// Platform admins must have explicit role assignments in the database
const DEFAULT_USER_ID = 'unknown';
const DEFAULT_USER_NAME = 'Unknown User';
const DEFAULT_USER_EMAIL = '';
// A permission read that fails during a page load is asked again, a few times.
const MAX_PERMISSION_LOAD_RETRIES = 5;
const PERMISSION_LOAD_RETRY_MS = 1500;
let permissionLoadRetries = 0;
const DEFAULT_ROLE: ActorRole | null = null; // No default role - capabilities come from DB assignments

// ============================================================================
// State Interface
// ============================================================================

interface UserState {
  // Current user context
  currentRole: ActorRole | null;
  /** @deprecated Tier is no longer used for authorization. */
  currentTier: number;
  userId: string;
  userName: string;
  userEmail: string;
  /** Public profile photo URL from user_rich_profiles (optional). */
  userAvatarUrl: string | null;
  
  // Impersonation state
  isImpersonating: boolean;
  originalUserId: string | null;
  originalUserName: string | null;
  impersonatedUserName: string | null;
  
  // Scoped permissions (new)
  groupMemberships: GroupMembership[];
  roleAssignments: ScopedRoleAssignment[];
  /** Resolved role names from role_assignments (e.g. workspace-admin, steward); used by nav and buildActorContext */
  assignedRoleNames: string[];
  /** User-pinned job roles for home dashboard composition (Build Contract §1D) */
  featuredJobRoleSlugs: string[];
  /** Capability IDs from DB role_permissions at current scope (UI/DB source of truth; no YAML) */
  resolvedCapabilityIds: string[];
  permissionsLoaded: boolean;
  permissionsLoading: boolean;
  permissionsError: string | null;
  
  // Current scope context (for contextual permission checks)
  currentScope: PermissionScope;
  
  // Sync state
  syncState: SyncState;
  
  // Supabase sync actions
  fetchFromSupabase: () => Promise<void>;
  syncUserProfileToSupabase: () => Promise<{ success: boolean; error?: string }>;
  
  // Legacy actions (for backward compatibility)
  switchRole: (role: ActorRole) => void;
  setUser: (userId: string, userName: string, email?: string) => void;
  setUserAvatarUrl: (url: string | null) => void;
  
  // Impersonation actions
  startImpersonation: (userId: string, userName: string, userEmail: string, role: ActorRole | string) => void;
  stopImpersonation: () => void;
  /** Clear impersonation state only (e.g. on real login). Keeps current userId/name/role; prevents rehydration as impersonated user. */
  clearImpersonationState: () => void;
  
  // New scoped permission actions
  loadPermissions: () => Promise<void>;
  setFeaturedJobRoleSlugs: (slugs: string[]) => void;
  saveFeaturedJobRoles: (slugs: string[]) => Promise<boolean>;
  setCurrentScope: (scope: PermissionScope) => void;
  can: (action: PermissionAction, scope?: PermissionScope) => boolean;
  canAny: (actions: PermissionAction[]) => boolean;
  canAsync: (action: PermissionAction, scope?: PermissionScope) => Promise<boolean>;
  getPermissionsAtScope: (scope: PermissionScope) => Promise<Map<PermissionAction, EffectivePermission>>;
  
  // Unified authorization (three-layer model: capability + scope + policy)
  canWithPolicy: (
    action: PermissionAction,
    resource?: UnifiedAuthInput['resource'],
    context?: UnifiedAuthInput['context']
  ) => UnifiedAuthResult;
  explainPermission: (
    action: PermissionAction,
    resource?: UnifiedAuthInput['resource'],
    context?: UnifiedAuthInput['context']
  ) => ExplainResult;
  
  // Navigation access (role-based)
  canAccessNavSection: (sectionId: NavSectionId) => boolean;
  canAccessNavItem: (path: string) => boolean;
  
  /** @deprecated Space access is now capability-gated */
  canAccessSpace: (spaceId: SystemModelingSpace) => boolean;
  /** @deprecated Use can('items.update') instead */
  canEdit: (spaceId?: SystemModelingSpace) => boolean;
  /** @deprecated Use can('items.create') instead */
  canCreate: () => boolean;
  /** @deprecated Use can('items.publish') instead */
  canPublish: () => boolean;
  canAdmin: () => boolean;
  
  // Utility
  reset: () => void;
  /** Clear persisted user selection and reset state (e.g. sign out when not using Clerk). */
  signOut: () => void;
  /** Clear the signed-in user from this browser when the session is already gone. Sends nothing to the server. */
  forgetSignedInUser: () => void;
}

// ============================================================================
// Default Values
// ============================================================================

const DEFAULT_SCOPE: PermissionScope = {
  kind: 'organization',
  id: '*',
};

/**
 * loadPermissions rebuilds this from scratch on every call (via `[...new Set(...)]`),
 * so it's a fresh array reference even when the resolved role names haven't changed.
 * Several independent effects call loadPermissions near-simultaneously on page load
 * (Layout.tsx), and every one of those was flowing straight into a `set()`, so any
 * consumer selecting `assignedRoleNames` (e.g. useSystemUsers) re-ran on each call —
 * observed as a visible flicker where dependent panels repeatedly re-fetch/re-render.
 */
function sameRoleNames(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((name) => setA.has(name));
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useUserStore = create<UserState>()(
  (set, get) => ({
    // Use persisted user selection if available, otherwise default to Wes Bright (Super Admin)
    // If impersonation is enabled and active, use impersonated user's details; else use selected user
    currentRole: getEffectiveImpersonation()
      ? toActorRole(getEffectiveImpersonation()!.impersonatedRole)
      : (persistedUser?.currentRole ? toActorRole(persistedUser.currentRole) : DEFAULT_ROLE),
    currentTier: 1, // @deprecated - tier is no longer used for authorization
    userId: getEffectiveImpersonation()
      ? getEffectiveImpersonation()!.impersonatedUserId
      : (persistedUser?.userId ?? DEFAULT_USER_ID),
    userName: getEffectiveImpersonation()
      ? getEffectiveImpersonation()!.impersonatedUserName
      : (persistedUser?.userName ?? DEFAULT_USER_NAME),
    userEmail: getEffectiveImpersonation()
      ? getEffectiveImpersonation()!.impersonatedUserEmail
      : (persistedUser?.userEmail ?? DEFAULT_USER_EMAIL),

    userAvatarUrl: null,
    
    // Impersonation state (only active when VITE_ENABLE_IMPERSONATION is true)
    isImpersonating: !!getEffectiveImpersonation(),
    originalUserId: getEffectiveImpersonation()?.originalUserId ?? null,
    originalUserName: getEffectiveImpersonation()?.originalUserName ?? null,
    impersonatedUserName: getEffectiveImpersonation()?.impersonatedUserName ?? null,
      
  // Scoped permissions (initially empty)
  groupMemberships: [],
  roleAssignments: [],
  /** Resolved role names from role_assignments (e.g. workspace-admin, steward); used by buildActorContext */
  assignedRoleNames: persistedUser?.assignedRoleNames ?? ([] as string[]),
  featuredJobRoleSlugs: [] as string[],
  resolvedCapabilityIds: [] as string[],
  permissionsLoaded: false,
    permissionsLoading: false,
    permissionsError: null,
    
    // Default to organization scope
    currentScope: DEFAULT_SCOPE,
    
    // Sync state
    syncState: createInitialSyncState(),
      
      // ========================================================================
      // Supabase Sync
      // ========================================================================
      
      fetchFromSupabase: async () => {
        if (!isSupabaseAvailable()) {
          console.log('[UserStore] Supabase not available');
          return;
        }
        
        const userId = get().userId;
        if (!userId) return;
        
        set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });
        
        const [assignmentsResult, membershipsResult] = await Promise.all([
          fetchAll<ScopedRoleAssignment>(ROLE_ASSIGNMENTS_TABLE, { column: 'created_at', ascending: false }, { filterByWorkspace: true, strictWorkspaceFilter: true }),
          fetchAll<GroupMembership>(GROUP_MEMBERSHIPS_TABLE, { column: 'created_at', ascending: false }, { filterByWorkspace: true, strictWorkspaceFilter: true }),
        ]);
        
        if (assignmentsResult.error || membershipsResult.error) {
          const error = assignmentsResult.error || membershipsResult.error;
          set({ syncState: { ...get().syncState, isLoading: false, syncError: error } });
          console.error('[UserStore] Failed to fetch from Supabase:', error);
          return;
        }
        
        // Filter to current user
        const userAssignments = (assignmentsResult.data || []).filter(a => a.userId === userId);
        const userMemberships = (membershipsResult.data || []).filter(m => m.userId === userId);
        
        set({ 
          roleAssignments: userAssignments,
          groupMemberships: userMemberships,
          syncState: { ...get().syncState, isLoading: false, lastSyncAt: new Date(), syncError: null }
        });
        console.log(`[UserStore] Loaded user data from Supabase`);
      },
      
      syncUserProfileToSupabase: async () => {
        if (!isSupabaseAvailable()) {
          return { success: true };
        }
        
        const { userId, userName, userEmail, currentRole } = get();
        
        const result = await updateRecord(USER_PROFILES_TABLE, userId, {
          id: userId,
          name: userName,
          email: userEmail,
          current_role: currentRole,
          updated_at: new Date(),
        });
        
        if (result.error) {
          console.error('[UserStore] Failed to sync user profile to Supabase:', result.error);
          return { success: false, error: result.error };
        }
        
        return { success: true };
      },
      
      // ========================================================================
      // Legacy Actions (backward compatibility)
      // ========================================================================
      
      switchRole: (role) => {
        // Just set the role - tier is deprecated and no longer used for authorization
        set({
          currentRole: role,
          // currentTier is deprecated but kept for backward compatibility
        });
        // Persist to sessionStorage for page refresh survival
        const state = get();
        saveUserSelection({
          userId: state.userId,
          userName: state.userName,
          userEmail: state.userEmail,
          currentRole: role,
          assignedRoleNames: state.assignedRoleNames,
        });
      },
      
      setUser: (userId, userName, email) => {
        const currentRole = get().currentRole;
        const prevUserId = get().userId;
        const userChanged = prevUserId !== userId;
        set({ 
          userId, 
          userName,
          userEmail: email || '',
          userAvatarUrl: null,
          // Only reset permissions when the user actually changes.
          // Re-confirming the same user (e.g. ensureWorkspaceContext re-running) should
          // not blow away already-loaded permissions and cause the "Checking..." stall.
          ...(userChanged ? {
            permissionsLoaded: false,
            groupMemberships: [],
            roleAssignments: [],
            assignedRoleNames: [],
          } : {}),
        });
        clearPermissionCache();
        // Persist to sessionStorage for page refresh survival
        saveUserSelection({
          userId,
          userName,
          userEmail: email || '',
          currentRole,
          assignedRoleNames: get().assignedRoleNames,
        });
      },

      setUserAvatarUrl: (url) => set({ userAvatarUrl: url }),
      
      // ========================================================================
      // Impersonation Actions
      // ========================================================================
      
      startImpersonation: (userId, userName, userEmail, role) => {
        if (!isImpersonationEnabled()) return;
        const state = get();
        const originalUserId = state.originalUserId || state.userId;
        const originalUserName = state.originalUserName || state.userName;
        const originalUserEmail = state.userEmail;
        const originalRole = state.currentRole;
        
        // Get current tenant/workspace from tenantStore
        // Import dynamically to avoid circular dependency
        let originalTenantId: string | null = null;
        let originalWorkspaceId: string | null = null;
        
        try {
          const tenantState = useTenantStore.getState();
          originalTenantId = tenantState.currentTenantId;
          originalWorkspaceId = tenantState.currentWorkspaceId;
        } catch (tenantErr) {
          console.warn('[UserStore] Could not get tenant state:', tenantErr);
        }
        
        // Normalize role - unknown values resolve to null (capabilities come from DB)
        const actorRole = toActorRole(typeof role === 'string' ? role : '');
        
        // Save impersonation state (including tenant context)
        const impersonationState: ImpersonationState = {
          isImpersonating: true,
          originalUserId,
          originalUserName,
          originalUserEmail,
          originalRole,
          originalTenantId,
          originalWorkspaceId,
          impersonatedUserId: userId,
          impersonatedUserName: userName,
          impersonatedUserEmail: userEmail,
          impersonatedRole: actorRole,
        };
        saveImpersonation(impersonationState);
        
        // Update store to impersonated user - use THEIR permissions, not admin's
        set({
          isImpersonating: true,
          originalUserId,
          originalUserName,
          impersonatedUserName: userName,
          userId,
          userName,
          userEmail,
          userAvatarUrl: null,
          currentRole: actorRole,
          // Reset permissions so they get reloaded for the impersonated user
          permissionsLoaded: false,
          groupMemberships: [],
          roleAssignments: [],
        });
        clearPermissionCache();
        
        console.log(`[UserStore] Started impersonation: ${userName} (${actorRole})`);
      },
      
      stopImpersonation: () => {
        if (!isImpersonationEnabled()) {
          saveImpersonation(null);
          return; // do not restore from storage when feature is off
        }
        const impersonation = loadImpersonation();
        if (!impersonation) return;
        
        // Clear impersonation state
        saveImpersonation(null);
        
        // Restore original user
        set({
          isImpersonating: false,
          originalUserId: null,
          originalUserName: null,
          impersonatedUserName: null,
          userId: impersonation.originalUserId,
          userName: impersonation.originalUserName,
          userEmail: impersonation.originalUserEmail,
          currentRole: impersonation.originalRole,
          // Reset permissions so they get reloaded for the original user
          permissionsLoaded: false,
          groupMemberships: [],
          roleAssignments: [],
        });
        clearPermissionCache();
        
        // Restore original tenant/workspace context
        if (impersonation.originalTenantId !== undefined) {
          const tenantStore = useTenantStore.getState();
          if (impersonation.originalTenantId) {
            tenantStore.setCurrentTenant(impersonation.originalTenantId);
          }
          if (impersonation.originalWorkspaceId) {
            tenantStore.setCurrentWorkspace(impersonation.originalWorkspaceId);
          }
          console.log(`[UserStore] Restored tenant context: ${impersonation.originalTenantId}`);
        }
        
        // Also restore the persisted user selection
        saveUserSelection({
          userId: impersonation.originalUserId,
          userName: impersonation.originalUserName,
          userEmail: impersonation.originalUserEmail,
          currentRole: impersonation.originalRole,
        });

        void (async () => {
          try {
            const supabase = getSupabaseClient();
            const { data: prof } = await supabase
              .from('user_rich_profiles')
              .select('avatar_url')
              .eq('user_id', impersonation.originalUserId)
              .maybeSingle();
            const raw = prof?.avatar_url as string | null | undefined;
            get().setUserAvatarUrl(typeof raw === 'string' && raw.length > 0 ? raw : null);
          } catch {
            get().setUserAvatarUrl(null);
          }
        })();
        
        console.log(`[UserStore] Stopped impersonation, restored: ${impersonation.originalUserName}`);
      },
      
      clearImpersonationState: () => {
        saveImpersonation(null);
        set({
          isImpersonating: false,
          originalUserId: null,
          originalUserName: null,
          impersonatedUserName: null,
          permissionsLoaded: false,
          groupMemberships: [],
          roleAssignments: [],
          assignedRoleNames: [],
          resolvedCapabilityIds: [],
        });
        clearPermissionCache();
        console.log('[UserStore] Cleared impersonation state (e.g. after real login)');
      },
      
      // ========================================================================
      // New Scoped Permission Actions
      // ========================================================================
      
      /**
       * Load all permissions for the current user from the database
       */
      loadPermissions: async () => {
        const { userId } = get();
        if (!userId) {
          // No user yet — mark loaded so UI gates resolve via role-based fallback
          set({ permissionsLoaded: true, permissionsLoading: false });
          return;
        }
        
        set({ permissionsLoading: true, permissionsError: null });

        // Snapshot the workspace ID at call-start. Used at the end to guard
        // against a race where this call started before the workspace was set
        // but a concurrent call already wrote capabilities with the correct scope.
        const workspaceIdAtCallStart = useTenantStore.getState().currentWorkspaceId ?? undefined;

        // The workspace's authored policies (Settings → Policies) follow the same
        // two triggers as permissions — session start and workspace switch — so
        // every person's app applies them, not only the admin's who saved them.
        void loadAuthoredPoliciesForWorkspace(workspaceIdAtCallStart ?? null);
        
        try {
          const data = await loadUserPermissionData(userId);
          if (data.loadFailed && permissionLoadRetries < MAX_PERMISSION_LOAD_RETRIES) {
            permissionLoadRetries += 1;
            setTimeout(() => void get().loadPermissions(), PERMISSION_LOAD_RETRY_MS);
          } else if (!data.loadFailed) {
            permissionLoadRetries = 0;
          }
          
          // Platform admins (super_admins table): run both checks in parallel so we don't add sequential latency
          const [bySupabaseId, byClerkId] = await Promise.all([
            isSuperAdminBySupabaseUserId(userId).catch(() => false),
            isSuperAdmin(userId).catch(() => false),
          ]);
          const workspaceId = useTenantStore.getState().currentWorkspaceId ?? undefined;

          let assignedRoleNames: string[] = [];
          if (data.roleAssignments.length > 0) {
            const supabase = getSupabaseClient();
            const roleIds = [...new Set(data.roleAssignments.map((ra) => ra.roleId))];
            const { data: roles } = await supabase.from('roles').select('id, name').in('id', roleIds);
            const idToName = new Map((roles || []).map((r: { id: string; name: string }) => [r.id, r.name]));
            assignedRoleNames = [
              ...new Set(
                data.roleAssignments.map((ra) => idToName.get(ra.roleId)).filter((n): n is string => !!n),
              ),
            ];
          }

          // When no role_assignments exist (e.g. demo workspaces that only use
          // workspace_memberships), fall back to the workspace membership role so
          // that owners and admins get the correct capability bundle.
          // Always fetch workspace membership role so that workspace_memberships.role = 'admin'
          // is not silently ignored when the user also has role_assignments (e.g. as a session participant).
          let workspaceMembershipRole: string | null = null;
          if (!bySupabaseId && !byClerkId && workspaceId) {
            try {
              const supabase = getSupabaseClient();
              const { data: membership } = await supabase
                .from('workspace_memberships')
                .select('role')
                .eq('user_id', userId)
                .eq('workspace_id', workspaceId)
                .maybeSingle();
              workspaceMembershipRole = (membership?.role as string | null) ?? null;
            } catch {
              // non-fatal
            }
          }

          const scope = workspaceId
            ? { kind: 'resource' as const, id: workspaceId, parentScope: { kind: 'organization' as const, id: '*' } }
            : { kind: 'organization' as const, id: '*' };
          const resolvedCapabilityIds = await getResolvedCapabilitiesForUser(userId, scope);

          // If workspace membership indicates admin, always include 'workspace-admin' in role names.
          // This ensures a user whose admin status is stored in workspace_memberships.role (not role_assignments)
          // still gets the correct capabilities even when other role_assignments exist.
          const membershipActorRole = toActorRole(workspaceMembershipRole ?? '');
          const isWorkspaceAdminViaMembership = membershipActorRole === 'workspace-admin' ||
            membershipActorRole === 'tenant-admin' ||
            membershipActorRole === 'platform-admin';

          const inferredRoleNames =
            bySupabaseId || byClerkId
              ? [...new Set(['platform-admin', ...assignedRoleNames])]
              : isWorkspaceAdminViaMembership
                ? [...new Set([membershipActorRole!, ...assignedRoleNames])]
                : assignedRoleNames;

          const featuredJobRoleSlugs = await loadFeaturedJobRoleSlugs(userId);

          // Workspace membership admin role always wins over a more specific (but less privileged)
          // role_assignment — prevents session-participant assignments from demoting a workspace admin.
          const nextCurrentRole =
            bySupabaseId || byClerkId
              ? 'platform-admin'
              : isWorkspaceAdminViaMembership
                ? membershipActorRole!
                // toActorRole returns null for unrecognized DB role names. Fall back to
                // the cached login role so nav/permissions stay intact.
                : (toActorRole(assignedRoleNames[0] ?? '') ?? get().currentRole);

          // Guard against the race where this call started without a workspace
          // (workspaceIdAtCallStart = undefined) but a concurrent call that had the
          // workspace ID has already written a complete capability set. In that case,
          // writing our org-scoped (incomplete) resolvedCapabilityIds would silently
          // strip workspace-role capabilities like CYCLE_CREATE. Skip the capability
          // write; the other call's result is authoritative.
          const nowWorkspaceId = useTenantStore.getState().currentWorkspaceId ?? undefined;
          const workspaceScopeUpgraded = !workspaceIdAtCallStart && !!nowWorkspaceId;
          const currentCaps = get().resolvedCapabilityIds;
          const skipCapabilityWrite = workspaceScopeUpgraded && currentCaps.length > 0;

          // The workspace arrived while this call was in flight, and nobody else
          // has written capabilities yet. What we resolved above was computed at
          // organization scope, where a role granted at `scope_kind = 'resource'`
          // (how every workspace role assignment is stored) applies to nothing —
          // so it is an empty set, and writing it leaves the person with no
          // capabilities for the rest of the session. Nothing re-runs this on a
          // workspace change, so an Item Manager stayed capability-less and every
          // items.edit check failed with MISSING_CAPABILITY. Resolve again now
          // that we know the workspace.
          let capabilityIdsToWrite = resolvedCapabilityIds;
          if (workspaceScopeUpgraded && currentCaps.length === 0 && nowWorkspaceId) {
            try {
              capabilityIdsToWrite = await getResolvedCapabilitiesForUser(userId, {
                kind: 'resource' as const,
                id: nowWorkspaceId,
                parentScope: { kind: 'organization' as const, id: '*' },
              });
            } catch {
              // Keep the org-scoped result rather than failing the whole load.
            }
          }

          // Keep the previous array reference when the resolved names are the same set,
          // so consumers gated on assignedRoleNames identity don't re-run for a no-op.
          const prevAssignedRoleNames = get().assignedRoleNames;
          const assignedRoleNamesToWrite = sameRoleNames(prevAssignedRoleNames, inferredRoleNames)
            ? prevAssignedRoleNames
            : inferredRoleNames;

          set({
            groupMemberships: data.groupMemberships,
            roleAssignments: data.roleAssignments,
            assignedRoleNames: assignedRoleNamesToWrite,
            featuredJobRoleSlugs,
            resolvedCapabilityIds: skipCapabilityWrite ? currentCaps : capabilityIdsToWrite,
            currentRole: nextCurrentRole,
            permissionsLoaded: true,
            permissionsLoading: false,
          });
          const st = get();
          saveUserSelection({
            userId: st.userId,
            userName: st.userName,
            userEmail: st.userEmail,
            currentRole: st.currentRole,
            assignedRoleNames: inferredRoleNames,
          });
        } catch (error) {
          console.error('Failed to load permissions:', error);
          // Still mark permissionsLoaded so UI gates (e.g. "Checking your permissions…")
          // resolve. The can() checker falls back to currentRole-based permissions when
          // resolvedCapabilityIds is empty, so role-based access still works correctly.
          set({
            permissionsError: error instanceof Error ? error.message : 'Unknown error',
            permissionsLoading: false,
            permissionsLoaded: true,
          });
        }
      },

      setFeaturedJobRoleSlugs: (slugs) => set({ featuredJobRoleSlugs: slugs }),

      saveFeaturedJobRoles: async (slugs) => {
        const { userId, assignedRoleNames } = get();
        if (!userId) return false;
        const assigned = jobSlugsFromAssignments(assignedRoleNames);
        const sanitized = [...new Set(slugs.filter((s) => assigned.includes(s)))];
        const result = await saveFeaturedJobRoleSlugs(userId, sanitized, assignedRoleNames);
        if (result.ok) set({ featuredJobRoleSlugs: sanitized });
        return result.ok;
      },
      
      /**
       * Set the current scope context for permission checks
       */
      setCurrentScope: (scope) => {
        set({ currentScope: scope });
      },
      
      /**
       * Synchronous permission check (uses cached data)
       * Falls back to role-based check if scoped permissions not loaded
       */
      can: (action, scope) => {
        const { permissionsLoaded, currentScope, currentRole, assignedRoleNames } = get();
        const targetScope = scope || currentScope;
        
        // If user is platform/system admin (by assignment), grant all permissions regardless of currentRole.
        // This ensures capability checks (e.g. session.pool.add) pass when policy engine uses this checker.
        if (assignedRoleNames?.includes('platform-admin') || assignedRoleNames?.includes('system-admin')) {
          return roleHasPermission('platform-admin', action);
        }
        
        // Platform and system admins by current role (they may have no scoped role_assignments)
        if (currentRole === 'platform-admin' || currentRole === 'system-admin') {
          return roleHasPermission(currentRole, action);
        }

        // If user holds a workspace-admin or tenant-admin role (via any assignment), check those
        // permissions. currentRole may be "workspace-member" when the user has multiple role
        // assignments and workspace-member sorts first — the fallback to currentRole alone
        // would silently strip workspace-admin capabilities such as values.accept.
        if (assignedRoleNames?.includes('workspace-admin')) {
          if (roleHasPermission('workspace-admin', action)) return true;
        }
        if (assignedRoleNames?.includes('tenant-admin')) {
          if (roleHasPermission('tenant-admin', action)) return true;
        }
        
        // If scoped permissions are loaded from database, check them first
        if (permissionsLoaded) {
          const scopedResult = canSync(action, targetScope);
          if (scopedResult) {
            return true;
          }
          // Fall back to role-based check when scoped permissions deny
          // This ensures the current role's baseline permissions still apply
        }
        
        // Fall back to Actor Role-based permission check (only if an admin role is set)
        if (currentRole) return roleHasPermission(currentRole, action);
        return false;
      },
      
      /**
       * Check if current role has ANY of the specified permissions
       */
      canAny: (actions: PermissionAction[]) => {
        const { permissionsLoaded, currentScope, currentRole, assignedRoleNames } = get();
        
        if (assignedRoleNames?.includes('platform-admin') || assignedRoleNames?.includes('system-admin')) {
          return roleHasAnyPermission('platform-admin', actions);
        }
        if (currentRole === 'platform-admin' || currentRole === 'system-admin') {
          return roleHasAnyPermission(currentRole, actions);
        }
        if (assignedRoleNames?.includes('workspace-admin')) {
          if (roleHasAnyPermission('workspace-admin', actions)) return true;
        }
        if (assignedRoleNames?.includes('tenant-admin')) {
          if (roleHasAnyPermission('tenant-admin', actions)) return true;
        }
        if (permissionsLoaded) {
          const scopedResult = actions.some(action => canSync(action, currentScope));
          if (scopedResult) {
            return true;
          }
          // Fall back to role-based check when scoped permissions deny
        }
        if (currentRole) return roleHasAnyPermission(currentRole, actions);
        return false;
      },
      
      /**
       * Check if current role can access a navigation section
       */
      canAccessNavSection: (sectionId: NavSectionId) => {
        const { permissionsLoaded, currentScope, currentRole } = get();
        if (currentRole === 'platform-admin' || currentRole === 'system-admin') {
          return roleCanAccessNavSection(currentRole, sectionId);
        }
        if (permissionsLoaded) {
          const perms = NAV_SECTION_PERMISSIONS[sectionId];
          if (perms?.length) return perms.some((p) => canSync(p, currentScope));
          return true;
        }
        const role = get().currentRole;
        if (role) return roleCanAccessNavSection(role, sectionId);
        return true; // permissive when no admin role is set
      },
      
      /**
       * Check if current role can access a specific nav item by path (uses effective permissions when loaded)
       */
      canAccessNavItem: (path: string) => {
        const { permissionsLoaded, currentScope, currentRole } = get();
        if (currentRole === 'platform-admin' || currentRole === 'system-admin') {
          return roleCanAccessNavItem(currentRole, path);
        }
        if (permissionsLoaded) {
          const perms = NAV_ITEM_PERMISSIONS[path];
          if (perms?.length) return perms.some((p) => canSync(p, currentScope));
          return true;
        }
        const role = get().currentRole;
        if (role) return roleCanAccessNavItem(role, path);
        return true; // permissive when no admin role is set
      },
      
      /**
       * Async permission check (fetches from database if needed)
       */
      canAsync: async (action, scope) => {
        const { userId, currentScope } = get();
        const targetScope = scope || currentScope;
        
        const result = await checkPermission(userId, action, targetScope);
        return result.allowed;
      },
      
      /**
       * Get all effective permissions at a scope
       */
      getPermissionsAtScope: async (scope) => {
        const { userId } = get();
        return getEffectivePermissions(userId, scope);
      },
      
      /**
       * Full three-layer authorization check (capability + scope + policy).
       * Use this when you have resource/context and want policy evaluation.
       */
      canWithPolicy: (action, resource, context) => {
        const { userId, currentRole } = get();
        const isAdmin = currentRole ? isPlatformOrSystemAdmin(currentRole) : false;
        
        return checkAuthorization({
          userId,
          userRoles: currentRole ? [currentRole] : [],
          isAdmin,
          action,
          resource,
          context,
        });
      },
      
      /**
       * Get a user-friendly explanation of why an action is allowed/denied.
       * Useful for showing "why is this disabled?" in the UI.
       */
      explainPermission: (action, resource, context) => {
        const { userId, currentRole } = get();
        const isAdmin = currentRole === 'system-admin';
        
        return explainAuthorization({
          userId,
          userRoles: currentRole ? [currentRole] : [],
          isAdmin,
          action,
          resource,
          context,
        });
      },
      
      // ========================================================================
      // Legacy Permission Checks (backward compatibility)
      // ========================================================================
      
      /** @deprecated Space access is now capability-gated */
      canAccessSpace: () => true,
      /** @deprecated Use can('items.update') instead */
      canEdit: () => get().can('items.update'),
      /** @deprecated Use can('items.create') instead */
      canCreate: () => get().can('items.create'),
      /** @deprecated Use can('items.publish') instead */
      canPublish: () => get().can('items.publish'),
      canAdmin: () => {
        const { currentRole } = get();
        return currentRole ? isPlatformOrSystemAdmin(currentRole) : false;
      },
      
      // ========================================================================
      // Utility
      // ========================================================================
      
      reset: () => {
        clearPermissionCache();
        set({
          currentRole: DEFAULT_ROLE,
          currentTier: 1,
          userId: DEFAULT_USER_ID,
          userName: DEFAULT_USER_NAME,
          userEmail: DEFAULT_USER_EMAIL,
          userAvatarUrl: null,
          groupMemberships: [],
          roleAssignments: [],
          assignedRoleNames: [],
          featuredJobRoleSlugs: [],
          resolvedCapabilityIds: [],
          permissionsLoaded: false,
          permissionsLoading: false,
          permissionsError: null,
          currentScope: DEFAULT_SCOPE,
        });
      },
      forgetSignedInUser: () => {
        forgetSavedUserSelection();
        get().reset();
      },
      signOut: () => {
        get().forgetSignedInUser();
        // End the Supabase session too. Before this, "Sign out" only cleared
        // the store; the session survived, and AuthSessionSync would (rightly)
        // adopt it again on the next load. Scope 'local' ends this browser's
        // session only: the default, 'global', also signs the account out of
        // every other browser and device.
        try {
          if (isSupabaseConfigured()) {
            void getSupabaseClient().auth.signOut({ scope: 'local' });
          }
        } catch (_) {
          // no client available — nothing to end
        }
      },
  })
);

// ============================================================================
// Selector Hooks
// ============================================================================

/**
 * Get the current user's group memberships
 */
export const useGroupMemberships = () => 
  useUserStore((state) => state.groupMemberships);

/**
 * Get the current user's role assignments
 */
export const useRoleAssignments = () => 
  useUserStore((state) => state.roleAssignments);

/**
 * Get the current scope context
 */
export const useCurrentScope = () => 
  useUserStore((state) => state.currentScope);

/**
 * Get permission loading state
 */
export const usePermissionsState = () => 
  useUserStore((state) => ({
    loaded: state.permissionsLoaded,
    loading: state.permissionsLoading,
    error: state.permissionsError,
  }));

// ============================================================================
// Capabilities follow the workspace
// ============================================================================
//
// Capabilities resolve at a scope, and every workspace role assignment is stored
// at `scope_kind = 'resource'` with the workspace id. The active workspace is
// resolved lazily at app start (a Layout effect, after child routes have already
// rendered), so a permission load that ran first resolved at organization scope,
// matched nothing, and wrote an empty set — and nothing re-ran it, so the person
// stayed capability-less for the whole session. An Item Manager was refused
// items.edit with MISSING_CAPABILITY on every item, which is what made the
// altitude questionnaire (and therefore Work Size, and therefore every effort
// estimate the planning engine reads) read-only.
//
// workspaceContextEvents is the bus built for this class of bug; its own comment
// says "a consumer that read null on first render keeps the empty result forever".
if (typeof window !== 'undefined') {
  subscribeWorkspaceContext(() => {
    const state = useUserStore.getState();
    if (state.userId) void state.loadPermissions();
  });

  // The workspace bus only speaks when the id *changes*, so a reload that
  // restores the same workspace is silent. Load once as soon as we know who the
  // person is: `useScopedPermissions` is the only other auto-loader and most
  // routes never mount it, which is why permissionsLoaded was still false on the
  // item page and the capability set stayed empty.
  let loadKickedFor: string | null = null;
  useUserStore.subscribe((state) => {
    if (!state.userId || state.permissionsLoading) return;
    if (state.permissionsLoaded && state.resolvedCapabilityIds.length > 0) return;
    if (loadKickedFor === state.userId) return;
    loadKickedFor = state.userId;
    void state.loadPermissions();
  });
  const initial = useUserStore.getState();
  if (initial.userId && initial.resolvedCapabilityIds.length === 0) {
    loadKickedFor = initial.userId;
    void initial.loadPermissions();
  }
}
